import { ArrowRight, Play } from "lucide-react";
import { TID } from "@/lib/testIds";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_aviation-integrity/artifacts/2b824wwp_Gemini_Generated_Image_2u7lyi2u7lyi2u7l.webp";

/**
 * Hero — light, editorial, Huberman-inspired layout. Massive serif headline
 * on the left, unretouched portrait on the right, two calm CTAs. All prior
 * campaign chrome (grid overlay, scan-line, status bar, stats row, ticker)
 * intentionally removed to lower visual density.
 */
const HeroCopy = () => (
  <div className="lg:col-span-7">
    <div className="flex items-center gap-3 mb-8 text-xs tracking-tight text-slate-500">
      <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />
      <span>ATFQ Podcast · Independent Watchdog</span>
      <span className="text-slate-300">·</span>
      <span>Broadcasting from Georgia</span>
    </div>

    <h1
      data-testid={TID.heroHeadline}
      className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight text-[#0A192F]"
    >
      Aviation-grade
      <br />
      <span className="text-[var(--accent)]">precision.</span>
      <br />
      <span className="text-slate-500 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl">
        An independent watchdog podcast.
      </span>
    </h1>

    <p
      data-testid={TID.heroSubheadline}
      className="mt-10 max-w-2xl text-lg md:text-xl text-slate-700 leading-relaxed"
    >
      <span className="text-[#0A192F] font-medium">
        Victor &ldquo;Vic&rdquo; Appiah
      </span>{" "}
      — independent voice, ATFQ podcast host, and civic watchdog.
      Broadcasting from Georgia to a national audience, with
      aviation-grade rigor for the questions institutions would rather
      not answer.
    </p>

    <div className="mt-10 flex flex-wrap gap-3">
      <a
        href="#atfq"
        data-testid={TID.heroPetitionBtn}
        className="group inline-flex items-center gap-3 bg-[var(--accent)] hover:bg-[#3b7bd0] text-white px-6 py-3.5 text-sm font-medium tracking-tight transition-colors"
      >
        <Play className="w-4 h-4 fill-current" />
        Listen to ATFQ
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </a>
      <a
        href="#atfq-ask"
        data-testid={TID.heroJoinBtn}
        className="group inline-flex items-center gap-3 border border-slate-300 hover:border-[#0A192F] text-[#0A192F] px-6 py-3.5 text-sm font-medium tracking-tight transition-colors"
      >
        Ask A Question
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </a>
    </div>

    <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs tracking-tight text-slate-500">
      <span>Host · ATFQ Podcast</span>
      <span className="text-slate-300">·</span>
      <span>FAA Telecom Ops Analyst</span>
      <span className="text-slate-300">·</span>
      <span>Licensed Commercial Pilot</span>
    </div>
  </div>
);

const HeroPortrait = () => (
  <div className="lg:col-span-5">
    <div className="relative aspect-square overflow-hidden bg-slate-100 border border-slate-200">
      <img
        src={PORTRAIT}
        alt="Vic Appiah in his podcast studio — microphone, headphones, and the ATFQ Answer The F**king Question road-sign on the wall behind him"
        data-testid={TID.heroPortrait}
        className="w-full h-full object-cover object-center"
        loading="eager"
      />
    </div>
    <div className="mt-4 flex items-center justify-between text-xs tracking-tight text-slate-500">
      <span>V. Appiah · In studio</span>
      <span>Independent · From Georgia</span>
    </div>
  </div>
);

export const Hero = () => (
  <section
    id="top"
    className="relative bg-white text-[#0A192F] overflow-hidden pt-36 md:pt-40"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <HeroCopy />
        <HeroPortrait />
      </div>
    </div>
  </section>
);

export default Hero;
